// InfrastructureDocumentation.js — Architecture & Infrastructure (refonte T1).
// Migrée sur le design system (chrome dark-aware) + ENRICHIE pour les nouveaux types d'agents
// (Genero, Database Oracle/Informix, API REST/SOAP, OS Linux/Windows, Web Browser) :
//  - sous-types d'agents (couleur/icône via les tokens partagés)
//  - protocoles de connexion étendus (SOAP, SSH, RDP, TNS, SQLI, WebDriver, GDC)
//  - affichage des cibles d'intégration (integrationTargets) + protocoles + authentification
//  - légende des types d'agents et stats « agents par type ».
// Mécanisme JSON conservé (require.context + loadFromContext). Rétro-compatible avec l'ancien JSON.
import React, { useMemo, useState } from 'react';
import {
  Server, Database, Shield, Globe, Cpu, HardDrive, Radio, Activity, Layers, MonitorCheck,
  Terminal, AppWindow, ZoomIn, ZoomOut, Maximize, AlertCircle,
} from 'lucide-react';
import { Card, CardContent, Button, EmptyState, Badge, Icon } from '../components/ui';
import { loadFromContext } from '../data/loadData';
import { AGENT_TYPES, agentOf } from '../theme/tokens';

const GRID_SIZE = 20;
const COMPONENT_WIDTH = GRID_SIZE * 12;
const COMPONENT_HEIGHT = GRID_SIZE * 8;
const ZOOM_STEP = 0.1;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2;

const infraContext = require.context('../data/infrastructure', false, /\.json$/);

// Types de composants (ajout du type AGENT, auparavant manquant).
const COMPONENT_TYPES = {
  PROXY: { icon: Shield, color: '#3B82F6', label: 'Proxy Server' },
  SERVER: { icon: Server, color: '#10B981', label: 'Application Server' },
  DATABASE: { icon: Database, color: '#6366F1', label: 'Database Server' },
  CACHE: { icon: HardDrive, color: '#F59E0B', label: 'Cache Server' },
  QUEUE: { icon: Radio, color: '#EC4899', label: 'Message Queue' },
  LOAD_BALANCER: { icon: Activity, color: '#8B5CF6', label: 'Load Balancer' },
  FIREWALL: { icon: Shield, color: '#EF4444', label: 'Firewall' },
  STORAGE: { icon: HardDrive, color: '#06B6D4', label: 'Storage Server' },
  AGENT: { icon: Cpu, color: '#EF4444', label: 'Agent Automata' },
  APPLICATION: { icon: AppWindow, color: '#0EA5E9', label: 'Application' },
};

// Protocoles de connexion (étendus : SOAP, SSH, RDP, TNS, SQLI, WebDriver, GDC).
const CONNECTION_TYPES = {
  HTTP: { name: 'HTTP/HTTPS', color: '#3B82F6', pattern: [1, 0], icon: Globe, label: 'Trafic Web', animationDuration: '10s' },
  'HTTP/HTTPS': { name: 'HTTP/HTTPS', color: '#3B82F6', pattern: [1, 0], icon: Globe, label: 'Trafic Web', animationDuration: '10s' },
  TCP: { name: 'TCP/IP', color: '#10B981', pattern: [5, 5], icon: Activity, label: 'Connexion TCP', animationDuration: '8s' },
  WebSocket: { name: 'WebSocket', color: '#8B5CF6', pattern: [3, 3], icon: Radio, label: 'Temps Réel', animationDuration: '6s' },
  UDP: { name: 'UDP', color: '#F59E0B', pattern: [2, 2], icon: Layers, label: 'Transfert Rapide', animationDuration: '6s' },
  INTERNAL: { name: 'Internal', color: '#EC4899', pattern: [4, 2], icon: Server, label: 'Communication Interne', animationDuration: '12s' },
  SOAP: { name: 'SOAP', color: '#059669', pattern: [6, 4], icon: Globe, label: 'Web Service SOAP', animationDuration: '8s' },
  SSH: { name: 'SSH', color: '#EAB308', pattern: [3, 2], icon: Terminal, label: 'Shell distant (Linux)', animationDuration: '7s' },
  RDP: { name: 'RDP/WinRM', color: '#2563EB', pattern: [4, 3], icon: MonitorCheck, label: 'Windows distant', animationDuration: '9s' },
  TNS: { name: 'Oracle TNS', color: '#F97316', pattern: [2, 4], icon: Database, label: 'Connexion Oracle', animationDuration: '8s' },
  SQLI: { name: 'Informix SQLI', color: '#06B6D4', pattern: [5, 2], icon: Database, label: 'Connexion Informix', animationDuration: '8s' },
  WebDriver: { name: 'WebDriver', color: '#EC4899', pattern: [1, 1], icon: AppWindow, label: 'Automatisation navigateur', animationDuration: '6s' },
  GDC: { name: 'Genero GDC', color: '#8B5CF6', pattern: [4, 4], icon: Server, label: 'Terminal Genero', animationDuration: '8s' },
};

const ZONE_COLORS = {
  dmz: '#FEF3C7', private_cloud: '#DBEAFE', public_cloud: '#D1FAE5', internal: '#E0E7FF', external: '#FEE2E2',
  external_zone: '#E5F3FF', frontend_zone: '#FFF7E6', backend_zone: '#FFF7E6', amp_zone: '#E5F3FF',
  database_zone: '#FDE7D3', api_zone: '#DCFCE7', os_zone: '#FEF9C3', browser_zone: '#FCE7F3',
};
const zoneColor = (zone) => ZONE_COLORS[zone] || '#F3F4F6';

// Résout couleur + nom d'icône d'un composant (priorité au sous-type d'agent).
const resolveVisual = (component) => {
  const agent = component.agentType ? agentOf(component.agentType) : null;
  const ct = COMPONENT_TYPES[component.type];
  return {
    color: agent?.color || ct?.color || '#6B7280',
    iconName: agent?.icon || null, // string (lucide) si agent
    IconCmp: ct?.icon || Server, // composant fallback
    agentLabel: agent?.label || null,
  };
};

// ---- Composant d'infrastructure --------------------------------------------
const InfraComponent = ({ component }) => {
  const [expanded, setExpanded] = useState(false);
  const { type, name, hostname, status, ip, specs, software = [], x, y, description, zone } = component;
  const { color, iconName, IconCmp, agentLabel } = resolveVisual(component);
  const targets = component.integrationTargets || [];
  const protocols = component.protocolsSupported || [];

  return (
    <div className="absolute transition-transform duration-200" style={{ transform: `translate(${x}px, ${y}px)`, width: COMPONENT_WIDTH, height: expanded ? 'auto' : COMPONENT_HEIGHT, zIndex: expanded ? 50 : 1 }}>
      <div
        className={`relative w-full h-full p-4 rounded-xl border-2 shadow-lg hover:shadow-xl transition-shadow ${status === 'active' ? 'border-green-300' : status === 'warning' ? 'border-yellow-300' : status === 'error' ? 'border-red-300' : 'border-gray-300'} ${expanded ? 'ring-2 ring-blue-400' : ''}`}
        style={{ backgroundColor: zoneColor(zone) }}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3 mb-3">
          {iconName ? <Icon name={iconName} className="w-6 h-6" style={{ color }} /> : <IconCmp className="w-6 h-6" style={{ color }} />}
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-800 truncate">{name}</h3>
            <p className="text-xs text-gray-500 truncate">{hostname}</p>
          </div>
          <span className={`ml-auto px-2 py-1 text-xs rounded-full font-medium ${status === 'active' ? 'bg-green-100 text-green-800' : status === 'warning' ? 'bg-yellow-100 text-yellow-800' : status === 'error' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>{status}</span>
        </div>

        <div className="space-y-2 text-sm">
          {agentLabel && (
            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: color }}>{agentLabel}</span>
          )}
          <div className="flex items-center gap-2"><Globe className="w-4 h-4 text-gray-400" /><span className="text-gray-600">{ip}</span></div>
          {zone && <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-gray-400" /><span className="text-gray-600">Zone: {zone}</span></div>}

          {expanded && (
            <div className="mt-3 space-y-4">
              {description && (
                <div className="border-t pt-3"><h4 className="font-medium text-gray-700 mb-1">Description</h4><p className="text-gray-600 text-sm">{description}</p></div>
              )}
              {protocols.length > 0 && (
                <div className="border-t pt-3">
                  <h4 className="font-medium text-gray-700 mb-2">Protocoles</h4>
                  <div className="flex flex-wrap gap-1">{protocols.map((p) => <span key={p} className="text-xs bg-white/70 text-gray-700 px-2 py-0.5 rounded border border-gray-200">{p}</span>)}</div>
                </div>
              )}
              {targets.length > 0 && (
                <div className="border-t pt-3">
                  <h4 className="font-medium text-gray-700 mb-2">Cibles d'intégration</h4>
                  <div className="space-y-1">
                    {targets.map((t, i) => (
                      <div key={i} className="text-xs bg-white/70 border border-gray-200 rounded p-2">
                        <span className="font-medium text-gray-700">{t.name}</span> <span className="text-gray-500">({t.type})</span>
                        <div className="text-gray-500">{t.protocol}{t.port ? `:${t.port}` : ''}{t.engine ? ` • ${t.engine}` : ''}{t.description ? ` — ${t.description}` : ''}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {component.authentication && (
                <div className="border-t pt-3"><h4 className="font-medium text-gray-700 mb-1">Authentification</h4><p className="text-gray-600 text-sm">{component.authentication.type}{component.authentication.credentialsStore ? ` • ${component.authentication.credentialsStore}` : ''}</p></div>
              )}
              {specs && (
                <div className="border-t pt-3">
                  <h4 className="font-medium text-gray-700 mb-2">Spécifications</h4>
                  <div className="grid grid-cols-1 gap-1">{Object.entries(specs).map(([k, v]) => <div key={k} className="flex items-center gap-2"><MonitorCheck className="w-4 h-4 text-gray-400" /><span className="text-gray-600">{k}: <span className="font-bold">{String(v)}</span></span></div>)}</div>
                </div>
              )}
              {software.length > 0 && (
                <div className="border-t pt-3">
                  <h4 className="font-medium text-gray-700 mb-2">Logiciels installés</h4>
                  <div className="space-y-2">{software.map((sw, i) => (
                    <div key={i} className="flex items-center justify-between bg-white/70 p-2 rounded">
                      <div className="flex items-center gap-2"><Layers className="w-4 h-4 text-gray-500" /><span className="text-gray-700 font-medium">{sw.name}</span><span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">{sw.version}</span></div>
                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${sw.status === 'running' ? 'bg-green-100 text-green-700' : sw.status === 'stopped' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>{sw.status}</span>
                    </div>
                  ))}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ZoomControls = ({ zoom, setZoom }) => (
  <div className="fixed bottom-4 right-4 flex gap-2 bg-white dark:bg-gray-800 p-2 rounded-lg shadow-lg z-50">
    <button onClick={() => setZoom((z) => Math.min(z + ZOOM_STEP, MAX_ZOOM))} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" title="Zoom avant"><ZoomIn size={20} /></button>
    <button onClick={() => setZoom((z) => Math.max(z - ZOOM_STEP, MIN_ZOOM))} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" title="Zoom arrière"><ZoomOut size={20} /></button>
    <button onClick={() => setZoom(1)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" title="Réinitialiser"><Maximize size={20} /></button>
    <span className="flex items-center px-2 text-sm text-gray-600 dark:text-gray-300">{Math.round(zoom * 100)}%</span>
  </div>
);

const GridBg = ({ zoom }) => {
  const g = GRID_SIZE * zoom;
  return (
    <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
      <defs><pattern id="grid" width={g} height={g} patternUnits="userSpaceOnUse"><path d={`M ${g} 0 L 0 0 0 ${g}`} fill="none" stroke="rgba(125,125,125,0.15)" strokeWidth="0.5" /></pattern></defs>
      <rect width="100%" height="100%" fill="url(#grid)" />
    </svg>
  );
};

const ConnectionLine = ({ source, target, points, type, allComponents, connectionData }) => {
  const [hovered, setHovered] = useState(false);
  const protocolType = connectionData?.protocol || type || 'TCP';
  const style = CONNECTION_TYPES[protocolType] || CONNECTION_TYPES.TCP;
  const startX = source.x + COMPONENT_WIDTH / 2;
  const startY = source.y + COMPONENT_HEIGHT / 2;
  const targetComponent = allComponents[target];
  if (!targetComponent) return null;
  const endX = targetComponent.x + COMPONENT_WIDTH / 2;
  const endY = targetComponent.y + COMPONENT_HEIGHT / 2;
  const ConnIcon = style.icon;
  let pathD = `M ${startX} ${startY}`;
  if (points && points.length) points.forEach((p) => { pathD += ` L ${p[0]} ${p[1]}`; });
  pathD += ` L ${endX} ${endY}`;
  const midX = (startX + endX) / 2;
  const midY = (startY + endY) / 2;

  return (
    <g onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} className="cursor-pointer" style={{ pointerEvents: 'all' }}>
      <path d={pathD} stroke={style.color} strokeWidth={hovered ? 4 : 3} fill="none" strokeDasharray={style.pattern} opacity={hovered ? '0.9' : '0.6'} />
      {[0, 1, 2].map((i) => (
        <circle key={i} r="4" fill={style.color} opacity="0.8">
          <animateMotion dur={style.animationDuration} repeatCount="indefinite" path={pathD} begin={`-${(parseFloat(style.animationDuration) * i) / 3}s`} />
        </circle>
      ))}
      <circle cx={startX} cy={startY} r="5" fill={style.color} />
      <circle cx={endX} cy={endY} r="5" fill={style.color} />
      <g transform={`translate(${midX - 12}, ${midY - 12})`}>
        <rect width="24" height="24" fill="white" stroke={style.color} strokeWidth="2" rx="6" />
        <foreignObject width="24" height="24"><div className="w-full h-full flex items-center justify-center"><ConnIcon className="w-4 h-4" style={{ color: style.color }} /></div></foreignObject>
      </g>
      {hovered && (
        <g transform={`translate(${midX}, ${midY - 40})`}>
          <rect x="-110" y="-40" width="220" height="80" fill="white" stroke={style.color} strokeWidth="1" rx="4" className="filter drop-shadow-lg" />
          <text textAnchor="middle" y="-20" className="text-sm font-medium fill-gray-700">{style.name}</text>
          <text textAnchor="middle" y="-5" className="text-xs fill-gray-500">{style.label}</text>
          <text textAnchor="middle" y="15" className="text-xs fill-gray-500">{`${source.name} → ${targetComponent.name}`}</text>
          {connectionData?.description && <text textAnchor="middle" y="30" className="text-xs fill-gray-400">{connectionData.description}</text>}
        </g>
      )}
    </g>
  );
};

const InfrastructureGrid = ({ data }) => {
  const [zoom, setZoom] = useState(1);
  if (!data || !data.components) {
    return <EmptyState icon={AlertCircle} title="Aucune donnée d'infrastructure" />;
  }
  const { components } = data;
  const contentStyle = { transform: `scale(${zoom})`, transformOrigin: '0 0', width: `${100 / zoom}%`, height: `${100 / zoom}%` };
  return (
    <div className="relative w-full h-screen bg-gray-50 dark:bg-gray-900 overflow-auto">
      <GridBg zoom={zoom} />
      <ZoomControls zoom={zoom} setZoom={setZoom} />
      <div style={contentStyle}>
        <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
          <g style={{ pointerEvents: 'all' }}>
            {Object.values(components).map((c) => c.connections?.map((conn, idx) => (
              <ConnectionLine key={`${c.id}-${conn.target}-${idx}`} source={c} target={conn.target} points={conn.points} type={conn.type} connectionData={conn} allComponents={components} />
            )))}
          </g>
        </svg>
        {Object.values(components).map((c) => <InfraComponent key={c.id} component={c} />)}
      </div>
    </div>
  );
};

// Statistiques + légende.
const InfrastructureStats = ({ data }) => {
  if (!data || !data.components) return null;
  const components = Object.values(data.components);
  const zones = [...new Set(components.map((c) => c.zone).filter(Boolean))];
  const types = [...new Set(components.map((c) => c.type))];
  const statusCounts = components.reduce((acc, c) => { acc[c.status] = (acc[c.status] || 0) + 1; return acc; }, {});
  const agentCounts = components.reduce((acc, c) => { if (c.agentType) acc[c.agentType] = (acc[c.agentType] || 0) + 1; return acc; }, {});
  const agentEntries = Object.entries(agentCounts);

  const stat = (value, label, cls = 'text-gray-900 dark:text-white') => (
    <Card><CardContent className="p-4 text-center"><div className={`text-2xl font-bold ${cls}`}>{value}</div><div className="text-sm text-gray-600 dark:text-gray-400">{label}</div></CardContent></Card>
  );

  return (
    <div className="space-y-4 mb-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {stat(components.length, 'Composants')}
        {stat(zones.length, 'Zones')}
        {stat(types.length, 'Types')}
        {stat(agentEntries.length, "Types d'agents")}
        {stat(statusCounts.active || 0, 'Actifs', 'text-green-600')}
      </div>
      {agentEntries.length > 0 && (
        <Card><CardContent className="p-4">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Agents par type</h4>
          <div className="flex flex-wrap gap-2">
            {agentEntries.map(([key, count]) => {
              const a = AGENT_TYPES[key];
              return <Badge key={key} color={a?.color}>{a ? <Icon name={a.icon} className="h-3 w-3" /> : null}{a?.label || key} · {count}</Badge>;
            })}
          </div>
        </CardContent></Card>
      )}
    </div>
  );
};

// ---- Page principale --------------------------------------------------------
const DocumentInfrastructure = () => {
  const infrastructures = useMemo(() => {
    return loadFromContext(infraContext)
      .filter((it) => it.data && it.data.components)
      .map((it) => ({ filename: it.filename, data: it.data, meta: it.data.metadata || {} }));
  }, []);

  const [selectedFilename, setSelectedFilename] = useState(infrastructures[0]?.filename || null);
  const selected = infrastructures.find((i) => i.filename === selectedFilename) || infrastructures[0] || null;

  if (infrastructures.length === 0) {
    return <EmptyState icon={Server} title="Aucune infrastructure" message="Ajoutez des fichiers dans src/data/infrastructure/." />;
  }

  const components = selected ? Object.values(selected.data.components || {}) : [];
  const zones = [...new Set(components.map((c) => c.zone).filter(Boolean))];
  const types = [...new Set(components.map((c) => c.type))];

  return (
    <div className="-m-6">
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Architecture &amp; Infrastructure</h1>
        <p className="mt-1 text-gray-600 dark:text-gray-300">{infrastructures.length} environnement(s) — composants, zones et agents.</p>

        <div className="mt-4 flex flex-wrap gap-2">
          {infrastructures.map((infra) => {
            const active = selected?.filename === infra.filename;
            return (
              <Button key={infra.filename} variant={active ? 'default' : 'outline'} size="sm" onClick={() => setSelectedFilename(infra.filename)}>
                <Layers className="h-4 w-4" />{infra.meta.title || infra.filename}
              </Button>
            );
          })}
        </div>
      </div>

      {selected && (
        <>
          <div className="px-6 py-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{selected.meta.title || selected.filename}</h2>
            {selected.meta.description && <p className="text-gray-600 dark:text-gray-300">{selected.meta.description}</p>}
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
              <span>{components.length} composants</span><span>•</span><span>{zones.length} zones</span><span>•</span><span>{types.length} types</span>
              {selected.meta.sourceRef && <><span>•</span><span className="font-mono">src: {selected.meta.sourceRef}</span></>}
            </div>
            <div className="mt-4"><InfrastructureStats data={selected.data} /></div>
          </div>
          <InfrastructureGrid data={selected.data} />
        </>
      )}
    </div>
  );
};

export default DocumentInfrastructure;
